import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Chip,
  // Button,
  // Divider,
} from "@mui/material";

import KitchenIcon from "@mui/icons-material/Kitchen";
import WeekendIcon from "@mui/icons-material/Weekend";
import BedIcon from "@mui/icons-material/Bed";
import RoofIcon from "@mui/icons-material/Roofing";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
// import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import interiorImg from "../../pictures/img10.jpg";

function InteriorService() {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const services = [
    {
      title: "Modular Kitchen",
      subtitle: "Elegant & Functional",
      desc: "Smart layouts, premium finishes, and space-saving storage tailored for everyday comfort.",
      icon: <KitchenIcon sx={{ fontSize: 34 }} />,
      accent: "#6d4c41",
      points: ["Soft-close cabinets", "Granite finish", "Smart storage"],
    },
    {
      title: "Living Room Design",
      subtitle: "Luxury First Impression",
      desc: "Create a warm and premium living experience with refined textures, lighting, and furniture balance.",
      icon: <WeekendIcon sx={{ fontSize: 34 }} />,
      accent: "#3949ab",
      points: ["TV unit design", "Mood lighting", "Feature wall"],
    },
    {
      title: "Bedroom Interiors",
      subtitle: "Comfort Meets Style",
      desc: "Personalized bedroom concepts with wardrobe integration, rich materials, and relaxing ambience.",
      icon: <BedIcon sx={{ fontSize: 34 }} />,
      accent: "#8e24aa",
      points: ["Custom wardrobes", "False ceiling", "Ambient look"],
    },
    {
      title: "False Ceiling Design",
      subtitle: "Clean Visual Depth",
      desc: "Layered ceiling concepts that improve lighting, elegance, and room character.",
      icon: <RoofIcon sx={{ fontSize: 34 }} />,
      accent: "#00897b",
      points: ["LED grooves", "Modern patterns", "Premium finish"],
    },
    {
      title: "3D Visualization",
      subtitle: "See Before Build",
      desc: "Experience your space with realistic 3D previews before execution begins.",
      icon: <ViewInArIcon sx={{ fontSize: 34 }} />,
      accent: "#ef6c00",
      points: ["3D walkthrough", "Material preview", "Layout approval"],
    },
    {
      title: "Custom Furniture",
      subtitle: "Made For Your Space",
      desc: "Tailor-made furniture solutions that match your room dimensions and design language.",
      icon: <DesignServicesIcon sx={{ fontSize: 34 }} />,
      accent: "#c62828",
      points: ["TV units", "Wardrobes", "Storage solutions"],
    },
  ];

  const highlights = [
    "Premium materials",
    "3D design support",
    "On-time delivery",
    "Custom finishes",
    "User-friendly planning",
    "Modern luxury concepts",
  ];

  const handleMove = (e) => {
    if (window.innerWidth < 900) return;

    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const rotateY = ((x / rect.width) - 0.5) * 14;
    const rotateX = -((y / rect.height) - 0.5) * 14;

    card.style.setProperty("--rx", `${rotateX}deg`);
    card.style.setProperty("--ry", `${rotateY}deg`);
  };

  const resetMove = (e) => {
    e.currentTarget.style.setProperty("--rx", "0deg");
    e.currentTarget.style.setProperty("--ry", "0deg");
  };

  return (
    <Box sx={{ background: "#f8f7fc" }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: 320, md: 460 },
          background: `linear-gradient(rgba(20,18,52,0.55), rgba(20,18,52,0.78)), url(${interiorImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            left: "8%",
            width: 220,
            height: 220,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,0.20), rgba(255,255,255,0.02))",
            filter: "blur(10px)",
            animation: "floatGlow 8s ease-in-out infinite",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            right: "8%",
            width: 180,
            height: 180,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,183,77,0.25), rgba(255,183,77,0.02))",
            filter: "blur(12px)",
            animation: "floatGlow 9s ease-in-out infinite 1.2s",
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 5 },
            borderRadius: 5,
            background: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.18)",
            backdropFilter: "blur(14px)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.22)",
            maxWidth: 900,
          }}
        >
          <Chip
            label="Premium Interior Design"
            sx={{
              mb: 2.5,
              bgcolor: "rgba(255,255,255,0.18)",
              color: "#fff",
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "2rem", md: "3.4rem" },
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1.15,
              letterSpacing: "0.02em",
            }}
          >
            Interior Design
          </Typography>

          <Typography
            sx={{
              mt: 2,
              color: "rgba(255,255,255,0.88)",
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.8,
              fontSize: { xs: "0.96rem", md: "1.08rem" },
            }}
          >
            We create elegant, practical, and luxurious interiors that feel
            personal, modern, and comfortable for everyday living.
          </Typography>

          <Box
            sx={{
              mt: 3.5,
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 1.2,
            }}
          >
            {highlights.map((item, index) => (
              <Chip
                key={index}
                label={item}
                sx={{
                  bgcolor: "rgba(255,255,255,0.14)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.16)",
                  fontWeight: 600,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Services Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background:
            "linear-gradient(135deg, #f8f7fc 0%, #eef1ff 45%, #fdfcff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 10% 20%, rgba(57,73,171,0.08), transparent 28%), radial-gradient(circle at 90% 80%, rgba(255,152,0,0.08), transparent 25%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Box textAlign="center" mb={8}>
            <Typography
              sx={{
                fontSize: { xs: "2rem", md: "3rem" },
                fontWeight: 800,
                color: "#171245",
                mb: 2,
              }}
            >
              Beautiful Interior Spaces
            </Typography>

            <Typography
              sx={{
                maxWidth: 760,
                mx: "auto",
                color: "#5f637a",
                lineHeight: 1.8,
                fontSize: "1rem",
              }}
            >
              Explore thoughtfully designed interior solutions built with
              premium aesthetics, smooth interaction, and a user-friendly
              experience from concept to completion.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} display="flex">
                <Card
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseMove={handleMove}
                  onMouseLeave={(e) => {
                    setHoveredIndex(null);
                    resetMove(e);
                  }}
                  sx={{
                    "--rx": "0deg",
                    "--ry": "0deg",
                    width: "100%",
                    borderRadius: 6,
                    background: "transparent",
                    boxShadow: "none",
                    overflow: "visible",
                    perspective: "1600px",
                    position: "relative",
                    cursor: "pointer",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: -8,
                      borderRadius: 7,
                      background: `linear-gradient(145deg, ${item.accent}22, rgba(255,255,255,0.2))`,
                      filter: "blur(18px)",
                      opacity: hoveredIndex === index ? 1 : 0.45,
                      transition: "all 0.35s ease",
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      minHeight: 360,
                      p: 4,
                      borderRadius: 6,
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      background:
                        "linear-gradient(145deg, rgba(255,255,255,0.92), rgba(245,247,255,0.78))",
                      border: "1px solid rgba(255,255,255,0.65)",
                      backdropFilter: "blur(18px)",
                      boxShadow: "0 18px 44px rgba(17,14,63,0.10)",
                      transformStyle: "preserve-3d",
                      transform:
                        hoveredIndex === index
                          ? "translateY(-14px) scale(1.03) rotateX(var(--rx)) rotateY(var(--ry))"
                          : "translateY(0px) scale(1) rotateX(0deg) rotateY(0deg)",
                      transition:
                        "transform 0.18s ease, box-shadow 0.32s ease, background 0.32s ease",
                      "&:hover": {
                        boxShadow: "0 24px 60px rgba(17,14,63,0.18)",
                        background:
                          "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(237,242,255,0.88))",
                      },
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          width: 78,
                          height: 78,
                          borderRadius: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          mb: 3,
                          background: `linear-gradient(145deg, ${item.accent}, #1f1f4d)`,
                          boxShadow: `0 14px 28px ${item.accent}40`,
                          transform: "translateZ(35px)",
                        }}
                      >
                        {item.icon}
                      </Box>

                      <Typography
                        sx={{
                          fontSize: { xs: "1.35rem", md: "1.5rem" },
                          fontWeight: 800,
                          color: "#171245",
                          lineHeight: 1.3,
                          mb: 1,
                          transform: "translateZ(24px)",
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        sx={{
                          color: item.accent,
                          fontWeight: 700,
                          mb: 2,
                          fontSize: "1rem",
                          transform: "translateZ(18px)",
                        }}
                      >
                        {item.subtitle}
                      </Typography>

                      <Typography
                        sx={{
                          color: "#5f637a",
                          lineHeight: 1.8,
                          fontSize: "0.97rem",
                          mb: 3,
                          transform: "translateZ(14px)",
                        }}
                      >
                        {item.desc}
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          transform: "translateZ(12px)",
                        }}
                      >
                        {item.points.map((point, idx) => (
                          <Chip
                            key={idx}
                            label={point}
                            size="small"
                            sx={{
                              bgcolor: `${item.accent}12`,
                              color: "#222",
                              fontWeight: 600,
                              border: `1px solid ${item.accent}22`,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    {/* <Box sx={{ mt: 3, transform: "translateZ(18px)" }}>
                      <Divider sx={{ mb: 2.2 }} />
                      <Button
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          px: 0,
                          fontWeight: 700,
                          color: "#171245",
                          textTransform: "none",
                          "&:hover": {
                            background: "transparent",
                            color: item.accent,
                          },
                        }}
                      >
                        Explore Design
                      </Button>
                    </Box> */}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Us Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(180deg, #fffdfb 0%, #f6f7ff 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Chip
                icon={<AutoAwesomeIcon />}
                label="Why Clients Choose Us"
                sx={{
                  mb: 2,
                  px: 1,
                  fontWeight: 700,
                  bgcolor: "#ede7f6",
                  color: "#4527a0",
                }}
              />

              <Typography
                sx={{
                  fontSize: { xs: "1.9rem", md: "2.7rem" },
                  fontWeight: 800,
                  color: "#171245",
                  lineHeight: 1.2,
                  mb: 2,
                }}
              >
                Designed for comfort, crafted for premium living
              </Typography>

              <Typography
                sx={{
                  color: "#5f637a",
                  lineHeight: 1.9,
                  fontSize: "1rem",
                }}
              >
                Our interior service focuses on elegant design, practical use of
                space, and high-end visual appeal while keeping the experience
                simple and comfortable for clients.
              </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container spacing={2.5}>
                {[
                  "3D preview before execution",
                  "Luxury modern finishes",
                  "Space-optimized layouts",
                  "Easy client-friendly process",
                  "Premium material suggestions",
                  "Personalized room concepts",
                ].map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        background:
                          "linear-gradient(145deg, rgba(255,255,255,0.95), rgba(245,247,255,0.85))",
                        border: "1px solid rgba(23,18,69,0.08)",
                        boxShadow: "0 10px 30px rgba(17,14,63,0.06)",
                        fontWeight: 600,
                        color: "#171245",
                      }}
                    >
                      {feature}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <style>
        {`
          @keyframes floatGlow {
            0%, 100% {
              transform: translateY(0px) scale(1);
            }
            50% {
              transform: translateY(-18px) scale(1.05);
            }
          }
        `}
      </style>
    </Box>
  );
}

export default InteriorService;